"use client";

import { useState, useEffect } from "react";
import { FieldArray, useFormikContext } from "formik";
import * as Yup from "yup";
import {
  Edit2,
  PlusCircle,
  Trash2,
  Save,
  CalendarIcon,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from "lucide-react";
import { format } from "date-fns";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";

// Validation schema for dependents
export const dependentValidationSchema = Yup.object({
  depFirstName: Yup.string()
    .trim()
    .required("First Name is required")
    .matches(
      /^[\u0600-\u06FFA-Za-z\s]*$/,
      "First name must not contain special characters or numbers"
    )
    .max(30, "First name must not exceed 30 characters"),
  depSecondName: Yup.string()
    .trim()
    .matches(
      /^[\u0600-\u06FFA-Za-z\s]*$/,
      "Second name must not contain special characters or numbers"
    )
    .max(30),
  depThirdName: Yup.string()
    .trim()
    .matches(
      /^[\u0600-\u06FFA-Za-z\s]*$/,
      "Third name must not contain special characters or numbers"
    )
    .max(30),
  depFourthName: Yup.string()
    .trim()
    .required("Last Name is required")
    .matches(
      /^[\u0600-\u06FFA-Za-z\s]*$/,
      "Fourth name must not contain special characters or numbers"
    )
    .max(30),
  depIdNumber: Yup.string()
    .trim()
    .matches(/^[\u0660-\u06690-9]*$/, "ID Number must contain only numbers")
    .max(15),
  idExpiry: Yup.date()
    .nullable()
    .transform((v) => (v === "" ? null : v))
    .typeError("Invalid date format")
    .min(new Date(), "ID Expiry must be in the future"),
  depPassportNo: Yup.string()
    .trim()
    .test(
      "not-only-letters",
      "Passport Number cannot contain only letters",
      (value) => !value || !/^[a-zA-Z\s]+$/.test(value)
    )
    .max(20),
  depPassportExpiry: Yup.date()
    .nullable()
    .transform((v) => (v === "" ? null : v))
    .typeError("Invalid date format")
    .min(new Date(), "Passport Expiry must be in the future"),
  depContactNo: Yup.string()
    .trim()
    .transform((value) =>
      value ? value.replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d)) : value
    )
    .matches(
      /^(00966|966|\+966|0)?5\d{8}$|^$/,
      "Enter a valid Saudi mobile number or leave empty"
    ),
  depDob: Yup.date()
    .nullable()
    .transform((v) => (v === "" ? null : v))
    .typeError("Invalid date format")
    .max(new Date(), "Date of birth cannot be in the future")
    .test(
      "realistic-age",
      "Please enter a realistic date of birth.",
      (value) => {
        if (!value) return true;
        const age = new Date().getFullYear() - new Date(value).getFullYear();
        return age <= 120;
      }
    ),
  depGender: Yup.string().oneOf(["Male", "Female", ""], "Invalid gender"),
  depCitizen: Yup.string().oneOf(
    ["true", "false", ""],
    "Invalid citizen value"
  ),
  depNationality: Yup.mixed().nullable(),
  depSponsorName: Yup.string()
    .trim()
    .matches(
      /^[\u0600-\u06FFA-Za-z\s]*$/,
      "Sponsor name must not contain special characters or numbers"
    )
    .max(50),
  depSponsorId: Yup.string()
    .nullable()
    .notRequired()
    .trim()
    .max(30, "Sponsor ID must not exceed 30 characters")
    .test(
      "is-valid-sponsor-id",
      "Sponsor ID must be alphanumeric and cannot contain only letters",
      (value) => {
        if (!value) return true; // allow null, undefined, or empty string
        return /^(?=.*\d)[A-Za-z0-9-]+$/.test(value);
      }
    ),
  depReligion: Yup.mixed().nullable(),
  depMaritalStatus: Yup.string().oneOf(
    ["Married", "Single", "Divorced", "Widowed", ""],
    "Invalid marital status"
  ),
  depRelation: Yup.mixed().nullable(),
});

// Empty dependent template
export const emptyDependent = {
  publicId: null,
  depFirstName: "",
  depSecondName: "",
  depThirdName: "",
  depFourthName: "",
  depIdNumber: "",
  idExpiry: null,
  depPassportNo: "",
  depPassportExpiry: null,
  depContactNo: "",
  depDob: null,
  depGender: "",
  depCitizen: "",
  depNationality: null,
  depSponsorName: "",
  depSponsorId: "",
  depReligion: null,
  depMaritalStatus: "",
  depRelation: null,
};

// Helper to map form values to API format
export const mapDependentToApiFormat = (member) => ({
  ...(member?.publicId && { publicId: member.publicId }),
  dependentFirstName: member.depFirstName,
  dependentSecondName: member.depSecondName,
  dependentThirdName: member.depThirdName,
  dependentFourthName: member.depFourthName,
  iqamaNo: member.depIdNumber,
  iqamaExpiryDate: member.idExpiry || "",
  passportNo: member.depPassportNo,
  passportExpiryDate: member.depPassportExpiry || "",
  contactNo: member.depContactNo,
  dateOfBirth: member.depDob || "",
  relation: member.depRelation?.value || "",
  religion: member.depReligion?.value || "",
  nationality: member.depNationality?.value || "",
  sponsorName: member.depSponsorName,
  sponsorId: member.depSponsorId,
  ...(member.depCitizen && { citizen: member.depCitizen }),
  ...(member.depMaritalStatus && { maritalStatus: member.depMaritalStatus }),
  ...(member?.depGender && { gender: member.depGender }),
});

// Helper to map API data to form format
export const mapApiToFormFormat = (dependent) => ({
  publicId: dependent.publicId || null,
  depFirstName: dependent.dependentFirstName || "",
  depSecondName: dependent.dependentSecondName || "",
  depThirdName: dependent.dependentThirdName || "",
  depFourthName: dependent.dependentFourthName || "",
  depIdNumber: dependent.iqamaNo || "",
  idExpiry: dependent.iqamaExpiryDate
    ? new Date(dependent.iqamaExpiryDate)
    : null,
  depPassportNo: dependent.passportNo || "",
  depPassportExpiry: dependent.passportExpiryDate
    ? new Date(dependent.passportExpiryDate)
    : null,
  depContactNo: dependent.contactNo || "",
  depDob: dependent.dateOfBirth ? new Date(dependent.dateOfBirth) : null,
  depGender: dependent.gender || "",
  depCitizen: dependent.citizen || "",
  depNationality: dependent.nationalityInfo
    ? {
        value: dependent.nationality || dependent.nationalityInfo.publicId,
        label: dependent.nationalityInfo.name,
      }
    : null,
  depSponsorName: dependent.sponsorName || "",
  depSponsorId: dependent.sponsorId || "",
  depReligion: dependent.religionInfo
    ? {
        value: dependent.religion || dependent.religionInfo.publicId,
        label: dependent.religionInfo.name,
      }
    : null,
  depMaritalStatus: dependent.maritalStatus || "",
  depRelation: dependent.relation
    ? {
        value: dependent.relation,
        label: dependent.relation,
      }
    : null,
});

// Custom error message component
const ErrorMessage = ({ name, errors, touched }) => {
  const { t, i18n } = useTranslation();

  const fieldPath = name.split(".");
  let error = errors;
  let touch = touched;

  // Navigate through nested objects
  for (const path of fieldPath) {
    error = error && error[path];
    touch = touch && touch[path];
  }

  return touch && error ? (
    <p className="text-sm text-red-500 mt-1">{t(error)}</p>
  ) : null;
};

// Debounce function
const deBounce = (func, delay) => {
  let debounceTimer;
  return function () {
    const args = arguments;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => func.apply(this, args), delay);
  };
};

// AsyncSelect styles
const asyncSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    height: "40px",
    borderRadius: "8px",
    borderColor: state.isFocused ? "#ddd" : "#ddd",
    boxShadow: state.isFocused ? "0 0 0 1px #ddd" : "none",
    "&:hover": {
      borderColor: "#ddd",
    },
    backgroundColor: state.isDisabled ? "#f9fafb" : "white",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 8px",
  }),
  input: (provided) => ({
    ...provided,
    margin: "0",
    padding: "0",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "40px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "14px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#f3f4f6"
      : state.isFocused
      ? "#f9fafb"
      : "white",
    color: "#111827",
    fontSize: "14px",
    cursor: "pointer",
    "&:active": {
      backgroundColor: "#f3f4f6",
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};

// Field definitions for the form
const FIELD_DEFINITIONS = [
  {
    id: "personalInfo",
    label: "Personal Information",
    fields: [
      { name: "depFirstName", label: "First Name", required: true },
      { name: "depSecondName", label: "Second Name" },
      { name: "depThirdName", label: "Third Name" },
      { name: "depFourthName", label: "Last Name", required: true },
    ],
  },
  {
    id: "contactInfo",
    label: "Contact Information",
    fields: [
      { name: "depContactNo", label: "Contact Number" },
      { name: "depPassportNo", label: "Passport Number" },
      { name: "depPassportExpiry", label: "Passport Expiry", type: "date" },
      { name: "depDob", label: "Date of Birth", type: "date" },
    ],
  },
  {
    id: "demographicInfo",
    label: "Demographic Information",
    fields: [
      { name: "depNationality", label: "Nationality", type: "select" },
      { name: "depReligion", label: "Religion", type: "select" },
      { name: "depRelation", label: "Relation", type: "select" },
      {
        name: "depGender",
        label: "Gender",
        type: "radio",
        options: [
          { value: "Male", label: "Male" },
          { value: "Female", label: "Female" },
        ],
      },
      {
        name: "depCitizen",
        label: "Citizen",
        type: "radio",
        options: [
          { value: "true", label: "Yes" },
          { value: "false", label: "No" },
        ],
      },
      {
        name: "depMaritalStatus",
        label: "Marital Status",
        type: "radio",
        options: [
          { value: "Married", label: "Married" },
          { value: "Single", label: "Single" },
        ],
      },
    ],
  },
  {
    id: "sponsorInfo",
    label: "Sponsor Information",
    fields: [
      { name: "depSponsorName", label: "Sponsor Name" },
      { name: "depSponsorId", label: "Sponsor ID" },
    ],
  },
];

export default function DependentsInfo({
  countryOptions = [],
  religionOptions = [],
  relationOptions = [],
  loadCountryOptions,
  loadReligionOptions,
  loadRelationOptions,
  readOnly = false,
  onSave = null,
  t = (text) => text, // Default translation function
}) {
  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleBlur,
    validateForm,
    setTouched,
  } = useFormikContext();
  const [activeMemberIndex, setActiveMemberIndex] = useState(null);
  const [dependentEditing, setDependentEditing] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [originalMember, setOriginalMember] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidationMessage, setShowValidationMessage] = useState(false);

  // Initialize with first member if available
  useEffect(() => {
    if (values?.members?.length > 0 && activeMemberIndex === null) {
      setActiveMemberIndex(0);
    }

    // Initialize all sections as expanded
    if (Object.keys(expandedSections).length === 0) {
      const initialExpandedState = {};
      FIELD_DEFINITIONS.forEach((section) => {
        initialExpandedState[section.id] = true;
      });
      setExpandedSections(initialExpandedState);
    }
  }, [values?.members, activeMemberIndex, expandedSections]);

  // Format date for display
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";
    if (typeof date === "string") {
      return date.split("T")[0];
    }
    return format(date, "yyyy-MM-dd");
  };

  const formatDateForDisplay = (date) => {
    if (!date) return "-";
    try {
      return format(new Date(date), "dd/MM/yyyy");
    } catch (e) {
      return "-";
    }
  };

  // Get full name of dependent
  const getFullName = (member) => {
    const parts = [
      member.depFirstName,
      member.depSecondName,
      member.depThirdName,
      member.depFourthName,
    ];
    return parts.filter(Boolean).join(" ");
  };

  // Debounce search functions
  const onSearchCountryDebounce = (inputValue, callback) => {
    deBounce(() => loadCountryOptions(inputValue, callback), 1000)();
  };

  const onSearchReligionDebounce = (inputValue, callback) => {
    deBounce(() => loadReligionOptions(inputValue, callback), 1000)();
  };

  const onSearchRelationDebounce = (inputValue, callback) => {
    deBounce(() => loadRelationOptions(inputValue, callback), 1000)();
  };

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Get field value for display
  const getFieldDisplayValue = (member, field) => {
    if (!member) return "-";

    const value = member[field.name];

    if (value === null || value === undefined || value === "") {
      return "-";
    }

    if (field.type === "date") {
      return formatDateForDisplay(value);
    }

    if (field.type === "select") {
      return value?.label || "-";
    }

    if (field.type === "radio") {
      if (field.name === "depCitizen") {
        return value === "true" ? "Yes" : "No";
      }
      return value || "-";
    }

    return value;
  };

  // Check if the current member has validation errors
  const validateCurrentMember = async () => {
    // Validate the entire form
    const formErrors = await validateForm();

    // Extract errors for the current member
    const memberErrors = {};
    let hasErrors = false;

    if (formErrors.members && formErrors.members[activeMemberIndex]) {
      Object.keys(formErrors.members[activeMemberIndex]).forEach((key) => {
        memberErrors[key] = formErrors.members[activeMemberIndex][key];
        hasErrors = true;
      });
    }

    setValidationErrors(memberErrors);

    // Touch all fields to show validation errors
    if (hasErrors) {
      const touchedFields = {};
      Object.keys(values.members[activeMemberIndex]).forEach((field) => {
        touchedFields[`members.${activeMemberIndex}.${field}`] = true;
      });
      setTouched(touchedFields, true);
      setShowValidationMessage(true);
    }

    return !hasErrors;
  };

  // Render form field based on type
  const renderFormField = (field, index) => {
    if (!values.members[activeMemberIndex]) return null;

    switch (field.type) {
      case "date":
        return (
          <div className="relative">
            <input
              type="date"
              id={`members[${activeMemberIndex}].${field.name}`}
              name={`members[${activeMemberIndex}].${field.name}`}
              value={
                values.members[activeMemberIndex][field.name]
                  ? formatDateToYYYYMMDD(
                      values.members[activeMemberIndex][field.name]
                    )
                  : ""
              }
              onChange={(e) => {
                const date = e.target.value ? new Date(e.target.value) : null;
                setFieldValue(
                  `members[${activeMemberIndex}].${field.name}`,
                  date
                );
              }}
              onBlur={handleBlur}
              disabled={!dependentEditing}
              className="w-full h-10 px-3 py-2 border border-[#DDD] rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-[#DDD] focus:border-[#DDD] disabled:bg-[#F9FAFB] disabled:text-[#475467] text-sm"
            />
            <CalendarIcon className="absolute right-3 top-2.5 h-4 w-4 text-[#475467] pointer-events-none" />
          </div>
        );

      case "select":
        let loadOptions, defaultOptions;

        if (field.name === "depNationality") {
          loadOptions = onSearchCountryDebounce;
          defaultOptions = countryOptions;
        } else if (field.name === "depReligion") {
          loadOptions = onSearchReligionDebounce;
          defaultOptions = religionOptions;
        } else if (field.name === "depRelation") {
          loadOptions = onSearchRelationDebounce;
          defaultOptions = relationOptions;
        }

        return (
          <AsyncSelect
            id={`members[${activeMemberIndex}].${field.name}`}
            name={`members[${activeMemberIndex}].${field.name}`}
            value={values.members[activeMemberIndex][field.name] || ""}
            onChange={(selectedOption) =>
              setFieldValue(
                `members[${activeMemberIndex}].${field.name}`,
                selectedOption
              )
            }
            loadOptions={loadOptions}
            defaultOptions={defaultOptions}
            styles={asyncSelectStyles}
            cacheOptions
            placeholder={t(`Select ${field.label}`)}
            isDisabled={!dependentEditing}
          />
        );

      case "radio":
        return (
          <div className="flex gap-4">
            {field.options.map((option) => (
              <label key={option.value} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name={`members[${activeMemberIndex}].${field.name}`}
                  value={option.value}
                  checked={
                    values.members[activeMemberIndex][field.name] ===
                    option.value
                  }
                  onChange={() =>
                    setFieldValue(
                      `members[${activeMemberIndex}].${field.name}`,
                      option.value
                    )
                  }
                  disabled={!dependentEditing}
                  className="h-4 w-4 text-[#475467] border-[#DDD] focus:ring-[#DDD]"
                />
                <span className="text-sm text-[#475467]">
                  {t(option.label)}
                </span>
              </label>
            ))}
          </div>
        );

      default:
        return (
          <input
            id={`members[${activeMemberIndex}].${field.name}`}
            name={`members[${activeMemberIndex}].${field.name}`}
            placeholder={t(`Enter ${field.label}`)}
            value={values.members[activeMemberIndex][field.name] || ""}
            onChange={(e) =>
              setFieldValue(
                `members[${activeMemberIndex}].${field.name}`,
                e.target.value
              )
            }
            onBlur={handleBlur}
            disabled={!dependentEditing}
            className="w-full h-10 px-3 py-2 border border-[#DDD] rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-[#DDD] focus:border-[#DDD] disabled:bg-[#F9FAFB] disabled:text-[#475467] text-sm"
          />
        );
    }
  };

  // Handle save button click
  const handleSave = async () => {
    const isValid = await validateCurrentMember();

    if (isValid) {
      setDependentEditing(false);
      setShowValidationMessage(false);
      if (onSave) onSave(values.members[activeMemberIndex], activeMemberIndex);
    }
  };

  return (
    <div className="w-full border rounded-lg shadow-sm bg-white">
      <div className="p-6 border-b flex flex-row items-center justify-between">
        <h2 className="text-xl font-medium text-[#04080B]">
          {t("Dependents Information")}
        </h2>
        {!readOnly && (
          <FieldArray name="members">
            {({ push }) => (
              <button
                type="button"
                onClick={() => {
                  push(emptyDependent);
                  setActiveMemberIndex(values.members?.length || 0);
                  setDependentEditing(true);
                  setOriginalMember(null);
                  setShowValidationMessage(false);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-[#F9FAFB] hover:bg-[#F2F4F7] text-[#475467] rounded-lg border border-[#F2F4F7]"
              >
                <PlusCircle className="h-4 w-4" />
                {t("Add Dependent")}
              </button>
            )}
          </FieldArray>
        )}
      </div>
      <div className="p-6">
        <FieldArray name="members">
          {({ push, remove }) => (
            <>
              {/* Members Summary Section */}
              {values?.members?.length > 0 && (
                <div className="space-y-2 mb-6">
                  {values.members.map((member, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-md border ${
                        activeMemberIndex === index
                          ? "border-[#DDD] bg-[#F9FAFB]"
                          : "border-[#DDD]"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#F9FAFB] flex items-center justify-center text-[#475467] font-medium">
                          {getFullName(member).charAt(0) || index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-[#04080B]">
                            {getFullName(member) ||
                              `${t("Member")} ${index + 1}`}
                          </p>
                          {member.depRelation && (
                            <p className="text-sm text-[#475467]">
                              {typeof member.depRelation === "object"
                                ? member.depRelation.label
                                : member.depRelation}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {!readOnly && (
                          <>
                            {(!dependentEditing ||
                              activeMemberIndex !== index) && (
                              <button
                                type="button"
                                className="p-1 text-[#475467] hover:text-[#04080B]"
                                onClick={() => {
                                  setActiveMemberIndex(index);
                                  setDependentEditing(true);
                                  setOriginalMember(
                                    JSON.parse(
                                      JSON.stringify(values.members[index])
                                    )
                                  );
                                  setShowValidationMessage(false);
                                }}
                              >
                                <Edit2 className="h-4 w-4" />
                                <span className="sr-only">{t("Edit")}</span>
                              </button>
                            )}
                            <button
                              type="button"
                              className="p-1 text-[#475467] hover:text-[#D32F2F]"
                              onClick={() => {
                                remove(index);
                                if (index === activeMemberIndex) {
                                  if (values.members.length > 1) {
                                    setActiveMemberIndex(
                                      index > 0 ? index - 1 : 0
                                    );
                                  } else {
                                    setActiveMemberIndex(null);
                                    setDependentEditing(false);
                                  }
                                } else if (index < activeMemberIndex) {
                                  setActiveMemberIndex(activeMemberIndex - 1);
                                }
                                setShowValidationMessage(false);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">{t("Remove")}</span>
                            </button>
                          </>
                        )}

                        {dependentEditing && activeMemberIndex === index && (
                          <>
                            <button
                              type="button"
                              className="px-3 py-1 text-sm bg-[#F9FAFB] hover:bg-[#F2F4F7] text-[#475467] border border-[#DDD] rounded mr-2"
                              onClick={() => {
                                // If we have original data, restore it
                                if (originalMember) {
                                  Object.keys(originalMember).forEach((key) => {
                                    setFieldValue(
                                      `members[${activeMemberIndex}].${key}`,
                                      originalMember[key]
                                    );
                                  });
                                }
                                setDependentEditing(false);
                                setShowValidationMessage(false);
                              }}
                            >
                              {t("Discard")}
                            </button>
                            <button
                              type="button"
                              className="px-3 py-1 text-sm bg-[#F9FAFB] hover:bg-[#F2F4F7] text-[#475467] border border-[#DDD] rounded"
                              onClick={handleSave}
                            >
                              <Save className="h-4 w-4 inline-block mr-1" />
                              {t("Save")}
                            </button>
                          </>
                        )}

                        <button
                          type="button"
                          className={`px-3 py-1 text-sm rounded ${
                            activeMemberIndex === index
                              ? "bg-[#F9FAFB] text-[#04080B] border border-[#DDD]"
                              : "bg-[#F9FAFB] hover:bg-[#F2F4F7] text-[#475467] border border-[#DDD]"
                          }`}
                          onClick={() => {
                            setActiveMemberIndex(
                              activeMemberIndex === index ? null : index
                            );
                            if (
                              dependentEditing &&
                              activeMemberIndex !== index
                            ) {
                              setDependentEditing(false);
                              setShowValidationMessage(false);
                            }
                          }}
                        >
                          {activeMemberIndex === index
                            ? t("Hide Details")
                            : t("Show Details")}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Input Section for Active Member */}
              {values?.members?.length > 0 && activeMemberIndex !== null && (
                <div className="border rounded-lg transition-all">
                  {dependentEditing ? (
                    // Edit Mode - Form View
                    <div className="p-6">
                      {/* Validation Error Message */}
                      {showValidationMessage &&
                        Object.keys(validationErrors).length > 0 && (
                          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                            <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-red-800">
                                {t("Please fix the following errors:")}
                              </p>
                              <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                                {Object.keys(validationErrors).map((key) => (
                                  <li key={key}>{t(validationErrors[key])}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FIELD_DEFINITIONS.flatMap((section) =>
                          section.fields.map((field) => (
                            <div key={field.name} className="space-y-2">
                              <label
                                htmlFor={`members[${activeMemberIndex}].${field.name}`}
                                className="block text-sm font-medium text-[#04080B]"
                              >
                                {t(field.label)}
                                {field.required && (
                                  <span className="text-[#D32F2F] ml-1">*</span>
                                )}
                              </label>
                              {renderFormField(field)}
                              <ErrorMessage
                                name={`members.${activeMemberIndex}.${field.name}`}
                                errors={errors}
                                touched={touched}
                              />
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ) : (
                    // Read-Only Mode - Display View
                    <div className="divide-y">
                      {FIELD_DEFINITIONS.map((section) => (
                        <div key={section.id} className="p-0">
                          <button
                            type="button"
                            className="w-full flex justify-between items-center p-4 text-left bg-[#F9FAFB] hover:bg-[#F2F4F7]"
                            onClick={() => toggleSection(section.id)}
                          >
                            <h3 className="text-base font-medium text-[#04080B]">
                              {t(section.label)}
                            </h3>
                            {expandedSections[section.id] ? (
                              <ChevronUp className="h-5 w-5 text-[#475467]" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-[#475467]" />
                            )}
                          </button>

                          {expandedSections[section.id] && (
                            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                              {section.fields.map((field) => (
                                <div key={field.name} className="flex flex-col">
                                  <span className="text-sm font-medium text-[#04080B]">
                                    {t(field.label)}
                                    {field.required && (
                                      <span className="text-[#D32F2F] ml-1">
                                        *
                                      </span>
                                    )}
                                  </span>
                                  <span className="text-sm text-[#475467] mt-1">
                                    {getFieldDisplayValue(
                                      values.members[activeMemberIndex],
                                      field
                                    )}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {values?.members?.length === 0 && (
                <div className="text-center py-8 text-[#475467]">
                  <p>{t("No dependents added yet.")}</p>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => {
                        push(emptyDependent);
                        setActiveMemberIndex(0);
                        setDependentEditing(true);
                        setShowValidationMessage(false);
                      }}
                      className="mt-4 px-4 py-2 bg-[#F9FAFB] hover:bg-[#F2F4F7] text-[#475467] rounded-lg border border-[#DDD] inline-flex items-center gap-2"
                    >
                      <PlusCircle className="h-4 w-4" />
                      {t("Add Dependent")}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </FieldArray>
      </div>
    </div>
  );
}
